import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFileText, FiDollarSign, FiUsers, FiTrendingUp } = FiIcons;

const WhyItMatters = () => {
  useEffect(() => {
    // SEO: Add structured data for this section
    const sectionData = {
      "@context": "https://schema.org",
      "@type": "WebPageElement",
      "name": "Why Enterprise AI Security Matters",
      "description": "Exploring the financial, regulatory, and reputational consequences of inadequate AI security."
    };
    
    const scriptId = 'why-it-matters-structured-data';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(sectionData);
  }, []);

  const consequences = [
    { icon: FiFileText, title: "Regulatory Compliance", subtitle: "GDPR, HIPAA, PCI Violations", description: "Fines up to 4% of annual revenue for data protection violations. Public AI creates compliance gaps.", impact: "€20M+ Average Fine" },
    { icon: FiDollarSign, title: "Financial Impact", subtitle: "Direct & Indirect Costs", description: "Average data breach costs $4.45M globally, plus legal fees, remediation, and regulatory penalties.", impact: "$4.45M Average Cost" },
    { icon: FiUsers, title: "Customer Trust", subtitle: "Brand Reputation Damage", description: "86% of customers lose trust after a data breach. Recovery can take years and cost millions in lost revenue.", impact: "86% Trust Loss" },
    { icon: FiTrendingUp, title: "Hidden API Costs", subtitle: "Escalating Expenses", description: "Public AI costs scale unpredictably. Enterprise usage can result in monthly bills exceeding $50K+ with no SLA guarantees.", impact: "$50K+ Monthly" }
  ];

  return (
    <section className="py-20 bg-white" itemScope itemType="https://schema.org/WebPageElement">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-6" itemProp="headline">Why Enterprise AI Security Matters</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" itemProp="description">The cost of inadequate AI security far exceeds the investment in proper protection. Here's what's at stake.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {consequences.map((item, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }} className="bg-gray-50 p-8 rounded-xl border border-gray-200 hover:border-red-300 transition-colors">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={item.icon} className="text-red-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-red-600 font-semibold text-sm mb-3">{item.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed mb-4">{item.description}</p>
                  <div className="inline-block bg-red-50 px-3 py-1 rounded-full">
                    <span className="text-red-800 font-semibold text-sm">{item.impact}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyItMatters;